import { useEffect, useMemo, useState } from "react";

const EVENT_SECTION_IDS = ["schedule", "builder", "roster"];

function normalizeEventSectionId(sectionId = "") {
  return sectionId === "list" || sectionId === "roster" ? "schedule" : sectionId;
}

function normalizeSearch(value) {
  return String(value || "").trim().toLowerCase();
}

function searchableText(...parts) {
  return parts.flat().filter(Boolean).join(" ").toLowerCase();
}

function chapterLabel(chapter = {}) {
  return [chapter.name, [chapter.city, chapter.region].filter(Boolean).join(", ")]
    .filter(Boolean)
    .join(" / ") || "Chapter";
}

function chapterDefaultLocation(chapter = {}) {
  return [
    chapter.meetingArea,
    chapter.defaultLocation,
    chapter.rangeName,
    [chapter.city, chapter.region].filter(Boolean).join(", "),
    chapter.name
  ]
    .map((value) => String(value || "").trim())
    .find(Boolean) || "";
}

function chapterVisibilityLabel(chapter = {}) {
  if (chapter.status && chapter.status !== "active") {
    return "Inactive";
  }
  return chapter.publicVisible === false ? "Private" : "Public";
}

function chapterSelectLabel(chapter = {}) {
  return `${chapterLabel(chapter)} - ${chapterVisibilityLabel(chapter)}`;
}

function chapterLeadName(chapter = {}) {
  return [
    chapter.leaderFirstName,
    chapter.leaderLastName
  ].filter(Boolean).join(" ").trim() ||
    (chapter.leaderEmails || []).find(Boolean) ||
    "";
}

function chapterLeadOptionValue(chapter = {}) {
  const name = chapterLeadName(chapter);
  const email = (chapter.leaderEmails || []).find(Boolean) || "";
  if (!name && !email) {
    return "";
  }
  return [
    name,
    email,
    "Chapter lead"
  ].filter(Boolean).join(" | ");
}

function eventChapterIds(event = {}) {
  return Array.isArray(event.chapterIds)
    ? event.chapterIds.filter(Boolean)
    : event.chapterId
      ? [event.chapterId]
      : [];
}

function memberChapterIds(member = {}) {
  return [
    member.homeChapterId,
    member.chapterId,
    member.chapterSnapshot?.chapterId,
    ...(Array.isArray(member.chapterIds) ? member.chapterIds : [])
  ]
    .map((chapterId) => String(chapterId || "").trim())
    .filter(Boolean)
    .filter((chapterId, index, list) => list.indexOf(chapterId) === index);
}

function memberVisibleForRoster(member = {}, chapterScopeRequired = false) {
  const effectiveStatus = member.expiration?.effectiveStatus || member.status;
  const reviewStatus = member.reviewSummary?.reviewStatus || member.review?.status;
  if (!effectiveStatus && chapterScopeRequired && member.chapterVisibleFields) {
    return true;
  }
  return ["active", "trial"].includes(String(effectiveStatus || "").toLowerCase()) && (!reviewStatus || reviewStatus === "approved");
}

const INTERNAL_DEMO_EVENT_COPY = /(?:chapter lead to owner|owner\/chapter|owner handoff|handoff note|workflow verification|participant notes|reaudit|demo|internal|\btest\b|test event|audit)/i;

function displayProgramTitle(title = "") {
  const cleanTitle = String(title || "").trim();
  if (!cleanTitle) {
    return "Published program";
  }
  if (!INTERNAL_DEMO_EVENT_COPY.test(cleanTitle)) {
    return cleanTitle;
  }
  const sanitized = cleanTitle
    .replace(/^Demo\s+/i, "")
    .replace(/\bTest\b/gi, "")
    .replace(/\bHandoff Drill\b/i, "Member Skills Session")
    .replace(/\bLead Reaudit\b/i, "Chapter Practice Session")
    .replace(/\bLead Final\b/i, "Chapter Training Session")
    .replace(/\s+\d{4}-\d{2}-\d{2}(?:\s+\d+)?$/i, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  return /^(?:jun|june|jul|july|aug|august|sep|september|oct|october|nov|november|dec|december|jan|january|feb|february|mar|march|apr|april|may)\s+\d/i.test(sanitized)
    ? "Chapter class"
    : sanitized || "Chapter class";
}

function displayInstructorName(instructor = {}) {
  const name = String(instructor.name || instructor.email || "").trim();
  if (!name) {
    return "Chapter instructor";
  }
  if (INTERNAL_DEMO_EVENT_COPY.test(name)) {
    return "Chapter instructor";
  }
  return name;
}

function formatEventDate(value) {
  if (!value) {
    return "Not scheduled";
  }
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function formatEventDay(value) {
  if (!value) {
    return "Unscheduled";
  }
  return new Date(value).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric"
  });
}

function formatEventTime(value) {
  if (!value) {
    return "";
  }
  return new Date(value).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit"
  });
}

function formatEventTimeRange(startsAt, endsAt) {
  const startTime = formatEventTime(startsAt);
  const endTime = formatEventTime(endsAt);
  return [startTime, endTime].filter(Boolean).join(" - ") || "Time TBD";
}

function formatDateTimeLocalInput(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function formatEventWindow(startsAt, endsAt) {
  if (!startsAt) {
    return "Time not set";
  }
  const start = new Date(startsAt);
  const end = endsAt ? new Date(endsAt) : null;
  const day = start.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric"
  });
  const startTime = formatEventTime(startsAt);
  const endTime = end && !Number.isNaN(end.getTime()) ? formatEventTime(endsAt) : "";
  return [day, [startTime, endTime].filter(Boolean).join(" - ")].filter(Boolean).join(" / ");
}

function eventMatchesFilters(item, filters = {}) {
  const query = normalizeSearch(filters.q);
  const chapterIds = eventChapterIds(item);
  const status = String(item.status || "").toLowerCase();
  const terminalNonCompletedStatuses = new Set(["cancelled", "rejected", "pending_approval", "draft"]);
  const eventEndTime = Date.parse(item.endsAt || item.startsAt || "");
  const completedByTime = Number.isFinite(eventEndTime) && eventEndTime < Date.now();
  const completed = status === "completed" || (completedByTime && !terminalNonCompletedStatuses.has(status));
  const upcoming = !["cancelled", "completed", "rejected"].includes(status) && !completedByTime;
  const haystack = searchableText(
    item.title,
    item.description,
    item.location,
    item.type,
    item.status,
    item.visibility,
    item.chapterId,
    chapterIds,
    item.chapterSnapshot?.name,
    item.chapterSnapshot?.city,
    item.chapterSnapshot?.region,
    item.instructors?.map((instructor) => [instructor.name, instructor.email, instructor.role].filter(Boolean).join(" ")),
    item.accessPolicy?.requiredQualificationKeys
  );

  return (
    (!filters.status ||
      (filters.status === "upcoming" ? upcoming : filters.status === "completed" ? completed : status === filters.status)) &&
    (!filters.visibility || item.visibility === filters.visibility) &&
    (!filters.type || item.type === filters.type) &&
    (!filters.resourceId || (item.resourceIds || []).includes(filters.resourceId)) &&
    (!filters.chapterId || chapterIds.length === 0 || chapterIds.includes(filters.chapterId)) &&
    (!query || haystack.includes(query))
  );
}

function formatLabel(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function eventStatusKey(event = {}) {
  const status = String(event.status || "").toLowerCase();
  if (event.changeRequest?.status === "pending") {
    return event.changeRequest.type === "cancellation" ? "cancellation_requested" : "time_change_requested";
  }
  if (status === "cancelled") {
    return "cancelled";
  }
  if (event.approval?.status === "pending" || event.status === "pending_approval") {
    return "pending_approval";
  }
  if (event.approval?.status === "rejected" || event.status === "rejected") {
    return "rejected";
  }
  if (event.completion?.status === "signed_off" || status === "completed") {
    return "completed";
  }
  if (event.status === "published" && event.approval?.status === "approved") {
    return "approved";
  }
  return event.status || "draft";
}

function eventStatusLabel(event = {}) {
  const key = eventStatusKey(event);
  const labels = {
    pending_approval: "Needs approval",
    approved: "Approved",
    cancellation_requested: "Cancellation requested",
    rejected: "Rejected",
    published: "Scheduled",
    time_change_requested: "Time change requested",
    completed: "Completed",
    cancelled: "Cancelled",
    draft: "Draft"
  };
  return labels[key] || formatLabel(key);
}

function rangeFeeLabel(rangeFee = {}) {
  const status = rangeFee?.status || "unpaid";
  const labels = {
    unpaid: "Collect range fee",
    paid: "Range fee paid",
    waived: "Fee waived",
    not_required: "No fee"
  };
  return labels[status] || formatLabel(status);
}

function rangeFeeRecordLabel(rangeFee = {}) {
  const status = rangeFee?.status || "unpaid";
  const labels = {
    unpaid: "Fee open",
    paid: "Paid",
    waived: "Waived",
    not_required: "No fee"
  };
  return labels[status] || formatLabel(status);
}

function defaultManualAttendanceForm() {
  return {
    fullName: "",
    email: "",
    status: "attended"
  };
}

function submitAttendanceOutcome(onRecordEventAttendance, registration, status) {
  onRecordEventAttendance(registration, status, "");
}

function updateAttendanceOutcome(onUpdateEventAttendance, attendance, status) {
  if (!onUpdateEventAttendance || !attendance?.attendanceId) {
    return;
  }
  onUpdateEventAttendance(attendance, { status });
}

export function EventsPage({
  canCreateEvent = true,
  canRequestClassCancellation = true,
  canRequestClassChanges = true,
  canSignOffEvent = false,
  eventAttendance,
  eventAttendanceFilters,
  eventAttendanceLoading,
  eventAttendanceSummary,
  editingEventId,
  eventFilters,
  eventForm,
  exportingEvents,
  eventRegistrationFilters,
  eventRegistrationsSummary,
  events,
  chapters = [],
  chaptersEnabled = false,
  chaptersLoading = false,
  chapterScopeRequired = false,
  loading,
  members = [],
  membersLoading = false,
  onCreateEvent,
  onCancelEventEdit,
  onEditEvent,
  onExportEvents,
  onRecordEventAttendance,
  onReviewEventApproval,
  onSignOffEvent,
  onUpdateEventAttendance,
  onUpdateEvent,
  recordingAttendanceKey,
  savingEvent,
  selectedEventId,
  selectedRange,
  canReviewEventApproval = false,
  setEventAttendanceFilters,
  setEventFilters,
  setEventForm,
  setEventRegistrationFilters,
  setSelectedEventId,
  summary,
  updatingEventId
}) {
  const [activeEventSection, setActiveEventSection] = useState(() => {
    if (typeof window === "undefined") {
      return "schedule";
    }
    const hash = normalizeEventSectionId(window.location.hash.replace("#events-", ""));
    return EVENT_SECTION_IDS.includes(hash) ? hash : "schedule";
  });
  const [programBuilderOpen, setProgramBuilderOpen] = useState(Boolean(editingEventId));
  const [handledEventLinkId, setHandledEventLinkId] = useState("");
  const [scheduleCatalogOpen, setScheduleCatalogOpen] = useState(false);
  const [rosterChecklistOpen, setRosterChecklistOpen] = useState(true);
  const [confirmingCancelEventId, setConfirmingCancelEventId] = useState("");
  const [confirmingApprovalRejectEventId, setConfirmingApprovalRejectEventId] = useState("");
  const [approvalDecisionNote, setApprovalDecisionNote] = useState("");
  const [classRequestMode, setClassRequestMode] = useState("");
  const [classRequestDraft, setClassRequestDraft] = useState({ startsAt: "", endsAt: "", reason: "" });
  const [chapterDefaultStatusApplied, setChapterDefaultStatusApplied] = useState(false);
  const [manualAttendanceOpen, setManualAttendanceOpen] = useState(false);
  const [manualAttendanceForm, setManualAttendanceForm] = useState(defaultManualAttendanceForm);
  const chapterOptionIds = useMemo(
    () => chapters.map((chapter) => chapter.chapterId).filter(Boolean),
    [chapters]
  );
  const defaultScopedChapter = chapters[0] || null;
  const defaultScopedChapterId = chapterScopeRequired ? defaultScopedChapter?.chapterId || "" : "";
  useEffect(() => {
    function syncEventSectionFromHash() {
      const hash = normalizeEventSectionId(window.location.hash.replace("#events-", ""));
      if (EVENT_SECTION_IDS.includes(hash)) {
        setActiveEventSection(hash);
      }
    }

    syncEventSectionFromHash();
    window.addEventListener("hashchange", syncEventSectionFromHash);
    return () => window.removeEventListener("hashchange", syncEventSectionFromHash);
  }, []);
  useEffect(() => {
    if (editingEventId) {
      setProgramBuilderOpen(true);
    }
  }, [editingEventId]);
  useEffect(() => {
    if (!chapterScopeRequired || chapterOptionIds.length === 0) {
      return;
    }
    setEventFilters((current) => (
      chapterOptionIds.includes(current.chapterId)
        ? current
        : { ...current, chapterId: defaultScopedChapterId }
    ));
  }, [chapterOptionIds, chapterScopeRequired, defaultScopedChapterId, setEventFilters]);
  useEffect(() => {
    if (!chapterScopeRequired || chapterDefaultStatusApplied) {
      return;
    }
    setEventFilters((current) => (
      current.status ? current : { ...current, status: "upcoming" }
    ));
    setChapterDefaultStatusApplied(true);
  }, [chapterDefaultStatusApplied, chapterScopeRequired, setEventFilters]);
  useEffect(() => {
    if (!chapterScopeRequired || !defaultScopedChapterId) {
      return;
    }
    setEventForm((current) => {
      const defaultLocation = chapterDefaultLocation(defaultScopedChapter);
      const shouldRefreshDefaultLocation = !current.location ||
        current.location === "Assigned chapter" ||
        current.location === current.primaryChapterId;
      if (chapterOptionIds.includes(current.primaryChapterId) && current.chapterScopeMode === "selected") {
        return shouldRefreshDefaultLocation && defaultLocation
          ? { ...current, location: defaultLocation }
          : current;
      }
      return {
        ...current,
        chapterScopeMode: "selected",
        chapterScopeIds: defaultScopedChapterId,
        primaryChapterId: defaultScopedChapterId,
        location: shouldRefreshDefaultLocation ? defaultLocation : current.location
      };
    });
  }, [chapterOptionIds, chapterScopeRequired, defaultScopedChapter, defaultScopedChapterId, setEventForm]);
  useEffect(() => {
    setRosterChecklistOpen(true);
  }, [selectedEventId]);
  useEffect(() => {
    if (activeEventSection !== "roster") {
      return;
    }
    setEventRegistrationFilters((current) => (
      current.status || current.paymentStatus || current.source
        ? { ...current, status: "", paymentStatus: "", source: "" }
        : current
    ));
  }, [activeEventSection, setEventRegistrationFilters]);
  useEffect(() => {
    if (typeof window === "undefined" || events.length === 0) {
      return;
    }
    const linkedEventId = new URLSearchParams(window.location.search).get("eventId") || "";
    if (!linkedEventId || linkedEventId === handledEventLinkId) {
      return;
    }
    const linkedEvent = events.find((item) => item.eventId === linkedEventId);
    if (!linkedEvent) {
      return;
    }
    const linkedSection = normalizeEventSectionId(window.location.hash.replace("#events-", ""));
    setSelectedEventId(linkedEventId);
    setActiveEventSection(EVENT_SECTION_IDS.includes(linkedSection) ? linkedSection : "schedule");
    if (linkedSection === "builder") {
      setProgramBuilderOpen(true);
      onEditEvent(linkedEvent);
    }
    setScheduleCatalogOpen(false);
    setHandledEventLinkId(linkedEventId);
  }, [events, handledEventLinkId, onEditEvent, setSelectedEventId]);

  const attendanceByEmail = useMemo(
    () => new Map(
      eventAttendance
        .filter((item) => item.email)
        .map((item) => [String(item.email).trim().toLowerCase(), item])
    ),
    [eventAttendance]
  );
  const attendanceByMemberId = useMemo(
    () => new Map(
      eventAttendance
        .filter((item) => item.memberId || item.memberSnapshot?.memberId)
        .map((item) => [String(item.memberId || item.memberSnapshot?.memberId).trim(), item])
    ),
    [eventAttendance]
  );
  const attendanceByName = useMemo(
    () => new Map(
      eventAttendance
        .filter((item) => normalizeSearch(item.fullName || item.attendeeName))
        .map((item) => [normalizeSearch(item.fullName || item.attendeeName), item])
    ),
    [eventAttendance]
  );
  const chapterById = useMemo(
    () => new Map(chapters.map((chapter) => [chapter.chapterId, chapter])),
    [chapters]
  );
  const selectedEvent = useMemo(
    () => events.find((item) => item.eventId === selectedEventId),
    [events, selectedEventId]
  );
  useEffect(() => {
    setClassRequestMode("");
    setClassRequestDraft({
      startsAt: formatDateTimeLocalInput(selectedEvent?.startsAt),
      endsAt: formatDateTimeLocalInput(selectedEvent?.endsAt),
      reason: ""
    });
  }, [selectedEvent?.eventId, selectedEvent?.startsAt, selectedEvent?.endsAt]);
  const selectedEventSignoff = selectedEvent?.completion?.status === "signed_off" ? selectedEvent.completion : null;
  const selectedNeedsOwnerDecision = Boolean(canReviewEventApproval && selectedEvent && (
    (selectedEvent.status === "pending_approval" && selectedEvent.approval?.status === "pending") ||
    selectedEvent.changeRequest?.status === "pending"
  ));
  const workspaceTemplateKey = String(selectedRange?.config?.templateKey || selectedRange?.templateKey || "").toLowerCase();
  const unscopedClassLabel = workspaceTemplateKey === "chapter_organization" ? "Organization-wide" : "Range-wide";
  const hideChapterSelect = chapterScopeRequired && chapters.length <= 1;
  const ownerCanAssignClassLead = chaptersEnabled && !chapterScopeRequired;
  const ownerCanCancelClass = String(selectedRange?.access?.role || "").toLowerCase() !== "chapter_leader";
  const classLeadOptions = useMemo(() => (
    chapters
      .map((chapter) => ({
        chapter,
        label: [
          chapterLeadName(chapter) || "Chapter lead",
          chapterLabel(chapter)
        ].filter(Boolean).join(" - "),
        value: chapterLeadOptionValue(chapter)
      }))
      .filter((option) => option.value)
  ), [chapters]);
  useEffect(() => {
    if (activeEventSection !== "builder" || !selectedEvent || editingEventId === selectedEvent.eventId) {
      return;
    }
    setProgramBuilderOpen(true);
    onEditEvent(selectedEvent);
  }, [activeEventSection, editingEventId, onEditEvent, selectedEvent]);
  const selectedEventChapterNames = useMemo(() => (
    selectedEvent
      ? eventChapterIds(selectedEvent).map((chapterId) => chapterById.get(chapterId)?.name || selectedEvent.chapterSnapshot?.name || "Chapter")
      : []
  ), [chapterById, selectedEvent]);
  const selectedEventIsChapterScoped = selectedEventChapterNames.length > 0;
  const selectedRosterChapterIds = useMemo(() => (
    selectedEvent ? eventChapterIds(selectedEvent) : []
  ), [selectedEvent]);
  const selectedRosterMembers = useMemo(() => {
    const q = String(eventRegistrationFilters.q || "").trim().toLowerCase();
    const chapterIds = new Set(selectedRosterChapterIds);
    return members
      .filter((member) => {
        if (!memberVisibleForRoster(member, chapterScopeRequired)) {
          return false;
        }
        if (chapterIds.size > 0) {
          if (!memberChapterIds(member).some((chapterId) => chapterIds.has(chapterId))) {
            return false;
          }
        }
        if (!q) {
          return true;
        }
        return [member.fullName, member.email, member.memberNumber, member.chapterSnapshot?.name]
          .some((value) => String(value || "").toLowerCase().includes(q));
      })
      .sort((left, right) => {
        if (selectedEventSignoff) {
          const leftEmail = String(left.email || "").trim().toLowerCase();
          const rightEmail = String(right.email || "").trim().toLowerCase();
          const leftName = normalizeSearch(left.fullName);
          const rightName = normalizeSearch(right.fullName);
          const leftRecorded = (leftEmail ? attendanceByEmail.has(leftEmail) : false) || (leftName ? attendanceByName.has(leftName) : false);
          const rightRecorded = (rightEmail ? attendanceByEmail.has(rightEmail) : false) || (rightName ? attendanceByName.has(rightName) : false);
          if (leftRecorded !== rightRecorded) {
            return leftRecorded ? -1 : 1;
          }
        }
        return String(left.fullName || left.email || "").localeCompare(String(right.fullName || right.email || ""));
      });
  }, [attendanceByEmail, attendanceByName, chapterScopeRequired, eventRegistrationFilters.q, members, selectedEventSignoff, selectedRosterChapterIds]);
  const rosterCheckedInCount = Number(eventAttendanceSummary?.attended || 0)
    + Number(eventAttendanceSummary?.noShow || 0)
    + Number(eventAttendanceSummary?.excused || 0);
  const rosterMemberCount = selectedRosterMembers.length;
  const rosterPendingCheckInCount = Math.max(rosterMemberCount - rosterCheckedInCount, 0);
  const rosterMemberEmailSet = new Set(
    selectedRosterMembers
      .map((member) => String(member.email || "").trim().toLowerCase())
      .filter(Boolean)
  );
  const rosterMemberIdSet = new Set(
    selectedRosterMembers
      .map((member) => String(member.memberId || "").trim())
      .filter(Boolean)
  );
  const rosterRecordedGuestCount = (eventAttendance || []).filter((attendance) => {
    const memberId = String(attendance.memberId || attendance.memberSnapshot?.memberId || "").trim();
    const email = String(attendance.email || "").trim().toLowerCase();
    if (attendance.attendeeType === "manual" || ["manual", "walk_in", "free_trial"].includes(attendance.source)) {
      return true;
    }
    if (memberId) {
      return !rosterMemberIdSet.has(memberId);
    }
    return email ? !rosterMemberEmailSet.has(email) : true;
  }).length;
  const selectedEventStatus = String(selectedEvent?.status || "").toLowerCase();
  const selectedEventCanCancel = Boolean(ownerCanCancelClass && selectedEventStatus === "published");
  const selectedEventApprovalPending = Boolean(
    selectedEvent &&
    (selectedEventStatus === "pending_approval" || selectedEvent.approval?.status === "pending")
  );
  const selectedEventChangePending = selectedEvent?.changeRequest?.status === "pending";
  const selectedEventAttendanceAvailable = Boolean(
    selectedEvent &&
    !selectedEventApprovalPending &&
    !selectedEventChangePending &&
    (selectedEventStatus === "published" || selectedEventStatus === "completed")
  );
  const selectedEventSignoffTitle = selectedEventApprovalPending
    ? "Waiting for owner approval"
    : selectedEventChangePending
      ? "Owner review pending"
      : selectedEventStatus === "cancelled"
        ? "Class cancelled"
        : selectedEventStatus === "rejected"
          ? "Request rejected"
          : selectedEventStatus === "draft"
            ? "Draft class"
            : selectedEventSignoff
              ? "Class signed off"
              : canSignOffEvent
                ? (selectedEventIsChapterScoped ? "Ready for chapter sign-off" : "Ready to close attendance")
                : selectedEventAttendanceAvailable
                  ? (selectedEventIsChapterScoped ? "Awaiting chapter sign-off" : "Attendance open")
                  : "Attendance not open";
  const selectedEventSignoffDetail = selectedEventApprovalPending
    ? "Attendance opens after the owner approves this class."
    : selectedEventChangePending
      ? "The owner needs to decide on the requested change."
      : selectedEventStatus === "cancelled"
        ? (selectedEvent.cancellationReason || "This class is no longer active.")
        : selectedEventStatus === "rejected"
          ? (selectedEvent.approval?.note || "This request was not approved.")
          : selectedEventStatus === "draft"
            ? "Publish the class before roster and attendance work begins."
            : selectedEventSignoff
              ? `${selectedEventSignoff.signedOffByName || "Staff"} / ${formatEventDate(selectedEventSignoff.signedOffAt)}`
              : canSignOffEvent
                ? `${rosterPendingCheckInCount} members still need attendance`
                : selectedEventAttendanceAvailable
                  ? (selectedEventIsChapterScoped
                    ? "Chapter lead closes attendance after check-in."
                    : "Track member and guest attendance from the roster.")
                  : "Roster and attendance are unavailable for this status.";
  const rosterWorklistTitle = selectedEventSignoff ? "Signed-off attendance" : "Check-in worklist";
  const rosterWorklistSummary = selectedEventSignoff
    ? `${rosterCheckedInCount} attendance records filed. Full roster listed below.`
    : rosterPendingCheckInCount > 0
      ? `${rosterPendingCheckInCount} members not checked in.`
      : "Everyone on the roster is recorded.";
  const rosterSearchLabel = selectedEventSignoff ? "Search record" : "Find attendee";
  const visibleScheduleEvents = useMemo(
    () => events
      .filter((item) => eventMatchesFilters(item, eventFilters))
      .sort((a, b) => (
        eventFilters.status === "completed"
          ? Date.parse(b.startsAt || 0) - Date.parse(a.startsAt || 0)
          : Date.parse(a.startsAt || 0) - Date.parse(b.startsAt || 0)
      )),
    [eventFilters, events]
  );
  const memberEmailSet = useMemo(
    () => new Set(members.map((member) => String(member.email || "").trim().toLowerCase()).filter(Boolean)),
    [members]
  );
  if (!selectedRange) {
    return null;
  }

  function switchEventSection(sectionId) {
    const nextSectionId = sectionId === "roster" ? "schedule" : sectionId;
    setActiveEventSection(nextSectionId);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#events-${nextSectionId}`);
    }
  }

  function updateSelectedEventUrl(eventId, sectionId = "schedule") {
    if (typeof window === "undefined") {
      return;
    }
    const url = new URL(window.location.href);
    if (eventId) {
      url.searchParams.set("eventId", eventId);
    } else {
      url.searchParams.delete("eventId");
    }
    url.hash = `events-${sectionId}`;
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  }

  function selectScheduleEvent(eventId) {
    setSelectedEventId(eventId);
    setScheduleCatalogOpen(false);
    setConfirmingCancelEventId("");
    setConfirmingApprovalRejectEventId("");
    updateSelectedEventUrl(eventId, "schedule");
  }

  function clearSelectedScheduleEvent() {
    setSelectedEventId("");
    setScheduleCatalogOpen(false);
    setConfirmingCancelEventId("");
    setConfirmingApprovalRejectEventId("");
    setApprovalDecisionNote("");
    updateSelectedEventUrl("", "schedule");
  }

  function applyChapterToEventForm(chapterId, current = {}, leadValue = undefined) {
    const selectedChapter = chapters.find((chapter) => chapter.chapterId === chapterId);
    const location = chapterId ? chapterDefaultLocation(selectedChapter) : "";
    return {
      ...current,
      chapterScopeMode: chapterId ? "selected" : "all",
      chapterScopeIds: chapterId,
      primaryChapterId: chapterId,
      location: chapterId ? location : current.location,
      instructorLines: leadValue === undefined
        ? (chapterId ? chapterLeadOptionValue(selectedChapter) : "")
        : leadValue
    };
  }

  function handleClassLeadChange(value) {
    const selectedOption = classLeadOptions.find((option) => option.value === value);
    setEventForm((current) => {
      if (!selectedOption) {
        return { ...current, instructorLines: "" };
      }
      return applyChapterToEventForm(selectedOption.chapter.chapterId, current, selectedOption.value);
    });
  }

  async function handleEventFormSubmit(event) {
    const saved = await onCreateEvent?.(event);
    if (!saved?.eventId) {
      return;
    }
    setProgramBuilderOpen(false);
    setScheduleCatalogOpen(false);
    setConfirmingCancelEventId("");
    setSelectedEventId(saved.eventId);
    switchEventSection("schedule");
    updateSelectedEventUrl(saved.eventId, "schedule");
  }

  function openClassRequest(mode) {
    setClassRequestMode(mode);
    setClassRequestDraft({
      startsAt: formatDateTimeLocalInput(selectedEvent?.startsAt),
      endsAt: formatDateTimeLocalInput(selectedEvent?.endsAt),
      reason: ""
    });
  }

  async function submitClassRequest(event) {
    event.preventDefault();
    if (!selectedEvent?.eventId || !classRequestMode || !classRequestDraft.reason.trim()) {
      return;
    }
    const patch = {
      requestType: classRequestMode,
      reason: classRequestDraft.reason.trim()
    };
    if (classRequestMode === "time_change") {
      patch.startsAt = new Date(classRequestDraft.startsAt).toISOString();
      patch.endsAt = new Date(classRequestDraft.endsAt).toISOString();
    }
    await onUpdateEvent?.(selectedEvent.eventId, patch);
    setClassRequestMode("");
    setClassRequestDraft((current) => ({ ...current, reason: "" }));
  }

  function attendanceSource(attendance = {}) {
    const email = String(attendance.email || "").trim().toLowerCase();
    if (attendance.source === "free_trial") {
      return "free_trial";
    }
    if (attendance.attendeeType === "manual" || ["manual", "walk_in"].includes(attendance.source)) {
      return "walk_in";
    }
    if (attendance.memberId || (email && memberEmailSet.has(email))) {
      return "member";
    }
    return "walk_in";
  }

  function submitManualAttendance(event) {
    event.preventDefault();
    const fullName = manualAttendanceForm.fullName.trim();
    const email = manualAttendanceForm.email.trim();
    if (!fullName && !email) {
      return;
    }
    const payload = {
      attendeeType: "manual",
      source: "free_trial",
      fullName,
      email
    };
    submitAttendanceOutcome(onRecordEventAttendance, payload, manualAttendanceForm.status || "attended");
    setManualAttendanceForm(defaultManualAttendanceForm());
    setManualAttendanceOpen(false);
  }

  return (
    <section className="panel module-panel events-workbench" id="events">
      {loading && <div className="status-strip events-loading-strip">Refreshing classes...</div>}

      <div className="events-workbench-command">
        <div>
          <span className="section-kicker">Workspace</span>
          <h3>Classes</h3>
          <small>
            {visibleScheduleEvents.length === events.length
              ? `${events.length} classes`
              : `${visibleScheduleEvents.length} visible / ${events.length} total`}
          </small>
        </div>
        <button
          type="button"
          className="event-create-program-button"
          disabled={!canCreateEvent && !editingEventId}
          onClick={() => {
            setProgramBuilderOpen(true);
            switchEventSection("schedule");
          }}
        >
          {editingEventId ? "Editing class" : "Create class"}
        </button>
      </div>

      {(programBuilderOpen || editingEventId) && (
        <>
      {!canCreateEvent && !editingEventId ? (
      <section className="membership-settings-panel events-program-builder-empty">
        <div className="events-builder-entry">
          <div>
            <span className="section-kicker">Class builder</span>
            <h3>Class requests are off</h3>
          </div>
        </div>
      </section>
      ) : !editingEventId && !programBuilderOpen ? (
      <section className="membership-settings-panel events-program-builder-empty">
        <div className="events-builder-entry">
          <div>
            <span className="section-kicker">Class builder</span>
            <h3>Start a class</h3>
          </div>
          <div className="events-builder-entry-actions">
            <button type="button" onClick={() => setProgramBuilderOpen(true)}>
              Start new class
            </button>
          </div>
        </div>
      </section>
      ) : (
      <form className="membership-settings-panel events-program-builder events-program-builder-simple" onSubmit={handleEventFormSubmit}>
        <div className="events-builder-entry">
          <div>
            <span className="section-kicker">Class builder</span>
            <h3>{editingEventId ? "Edit class" : "Create class"}</h3>
          </div>
        </div>
        <div className="events-builder-layout events-builder-simple-layout">
          <section className="events-builder-card events-builder-simple-card">
            <label>
              Name
              <input
                value={eventForm.title}
                onChange={(event) => setEventForm((current) => ({ ...current, title: event.target.value }))}
                placeholder="New shooter orientation"
              />
            </label>
            <label>
              Description
              <textarea
                value={eventForm.description}
                onChange={(event) => setEventForm((current) => ({ ...current, description: event.target.value }))}
                placeholder="Short member-facing summary"
              />
            </label>
            <div className="form-grid events-builder-simple-grid">
              {chaptersEnabled && !hideChapterSelect && (
                <label>
                  Chapter
                  <select
                    value={eventForm.primaryChapterId || ""}
                    onChange={(event) => {
                      const chapterId = event.target.value;
                      setEventForm((current) => applyChapterToEventForm(chapterId, current));
                    }}
                    disabled={chaptersLoading || (chapterScopeRequired && chapters.length <= 1)}
                  >
                    {!chapterScopeRequired && <option value="">{unscopedClassLabel}</option>}
                    {chapters.map((chapter) => (
                      <option key={chapter.chapterId} value={chapter.chapterId}>{chapterSelectLabel(chapter)}</option>
                    ))}
                  </select>
                </label>
              )}
              {ownerCanAssignClassLead && (
                <label>
                  Class lead
                  <select
                    value={eventForm.instructorLines || ""}
                    onChange={(event) => handleClassLeadChange(event.target.value)}
                    disabled={classLeadOptions.length === 0}
                  >
                    <option value="">Not assigned</option>
                    {classLeadOptions.map((option) => (
                      <option key={`${option.chapter.chapterId}:${option.value}`} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                Starts
                <input
                  type="datetime-local"
                  value={eventForm.startsAt}
                  onChange={(event) => setEventForm((current) => ({ ...current, startsAt: event.target.value }))}
                />
              </label>
              <label>
                Ends
                <input
                  type="datetime-local"
                  value={eventForm.endsAt}
                  onChange={(event) => setEventForm((current) => ({ ...current, endsAt: event.target.value }))}
                />
              </label>
              <label>
                Location
                <input
                  value={eventForm.location}
                  onChange={(event) => setEventForm((current) => ({ ...current, location: event.target.value }))}
                  placeholder="Bay 1, classroom, or offsite location"
                />
              </label>
            </div>
          </section>
        </div>
        <div className="form-actions">
          <button type="submit" disabled={savingEvent || !eventForm.title.trim()}>
            {savingEvent
              ? "Saving..."
              : editingEventId
                ? "Save class"
                : chapterScopeRequired
                  ? "Submit for approval"
                  : "Create class"}
          </button>
          {editingEventId && (
            <button type="button" className="secondary-button" onClick={() => {
              onCancelEventEdit();
              setProgramBuilderOpen(false);
              switchEventSection("schedule");
            }}>
              Cancel edit
            </button>
          )}
          {!editingEventId && (
            <button type="button" className="secondary-button" onClick={() => setProgramBuilderOpen(false)}>
              Cancel
            </button>
          )}
        </div>
      </form>
      )}

        </>
      )}

      <div className="member-list events-schedule-list">
        <div className="panel-header">
          <div>
            <h3>{selectedEvent ? "Selected class" : "Class list"}</h3>
          </div>
        </div>
        {selectedEvent && (
          <section className="events-schedule-focus" aria-label="Selected class">
            <div className="events-schedule-focus-main">
              <span className="section-kicker">Selected class</span>
              <h4>{displayProgramTitle(selectedEvent.title)}</h4>
              <div className="events-schedule-focus-facts">
                <span>{formatEventWindow(selectedEvent.startsAt, selectedEvent.endsAt)}</span>
                <span>{selectedEvent.location || "Location not set"}</span>
                <span>{selectedEventChapterNames.length > 0 ? selectedEventChapterNames.join(", ") : unscopedClassLabel}</span>
              </div>
	              <div className={`events-schedule-focus-state is-${eventStatusKey(selectedEvent)}`}>
	                <strong>{eventStatusLabel(selectedEvent)}</strong>
                    {selectedEvent.approval?.submittedByName && <span>Requested by {selectedEvent.approval.submittedByName}</span>}
              </div>
              <div className={`events-signoff-state ${selectedEventSignoff ? "is-complete" : "is-pending"}`}>
                <strong>{selectedEventSignoffTitle}</strong>
                <span>{selectedEventSignoffDetail}</span>
              </div>
              {selectedEvent.changeRequest?.status === "pending" && (
                <div className="events-schedule-focus-request-note">
                  <strong>{selectedEvent.changeRequest.type === "cancellation" ? "Cancellation requested" : "Time change requested"}</strong>
                  <span>{selectedEvent.changeRequest.reason}</span>
                  {selectedEvent.changeRequest.type === "time_change" && (
                    <small>{formatEventWindow(selectedEvent.changeRequest.startsAt, selectedEvent.changeRequest.endsAt)}</small>
                  )}
                </div>
              )}
              {selectedNeedsOwnerDecision && (
                <div className="events-approval-decision-panel">
                  <div className="events-approval-decision-copy">
                    <span>
                      {selectedEvent.changeRequest?.status === "pending"
                        ? selectedEvent.changeRequest.type === "cancellation"
                          ? "Cancellation request"
                          : "Time change request"
                        : "Owner approval"}
                    </span>
                    <strong>
                      {selectedEvent.changeRequest?.status === "pending"
                        ? "Review this requested change"
                        : "Review this class before it is published"}
                    </strong>
                    <small>
                      {selectedEvent.changeRequest?.status === "pending"
                        ? selectedEvent.changeRequest.reason || "No reason provided"
                        : selectedEvent.approval?.submittedByName
                          ? `Submitted by ${selectedEvent.approval.submittedByName}`
                          : "Submitted by chapter lead"}
                    </small>
                    {selectedEvent.changeRequest?.type === "time_change" && (
                      <small>{formatEventWindow(selectedEvent.changeRequest.startsAt, selectedEvent.changeRequest.endsAt)}</small>
                    )}
                  </div>
                  <label>
                    Decision note
                    <textarea
                      value={approvalDecisionNote}
                      onChange={(event) => setApprovalDecisionNote(event.target.value)}
                      placeholder="Optional note for the chapter lead"
                      rows={2}
                    />
                  </label>
                  <div className="events-approval-decision-actions">
                    <button
                      type="button"
                      className="events-approval-approve-button"
                      disabled={updatingEventId === selectedEvent.eventId}
                      onClick={() => {
                        setConfirmingApprovalRejectEventId("");
                        onReviewEventApproval?.(selectedEvent.eventId, "approved", { note: approvalDecisionNote });
                        setApprovalDecisionNote("");
                      }}
                    >
                      Approve class
                    </button>
                    <button
                      type="button"
                      className={confirmingApprovalRejectEventId === selectedEvent.eventId ? "danger-button" : "secondary-button"}
                      disabled={updatingEventId === selectedEvent.eventId}
                      onClick={() => {
                        if (confirmingApprovalRejectEventId !== selectedEvent.eventId) {
                          setConfirmingApprovalRejectEventId(selectedEvent.eventId);
                          return;
                        }
                        setConfirmingApprovalRejectEventId("");
                        onReviewEventApproval?.(selectedEvent.eventId, "rejected", { note: approvalDecisionNote });
                        setApprovalDecisionNote("");
                      }}
                    >
                      {confirmingApprovalRejectEventId === selectedEvent.eventId ? "Confirm reject" : "Reject request"}
                    </button>
                  </div>
                </div>
              )}
	            </div>
            <div className="events-schedule-focus-actions">
              {!chapterScopeRequired && (
                <button
                  type="button"
                  onClick={() => {
                    setProgramBuilderOpen(true);
                    onEditEvent(selectedEvent);
                    switchEventSection("schedule");
                  }}
                >
                  Edit class
                </button>
              )}
              <button
                type="button"
                className="secondary-button"
                onClick={clearSelectedScheduleEvent}
              >
                All classes
              </button>
              {chapterScopeRequired && selectedEvent.status === "published" && selectedEvent.changeRequest?.status !== "pending" && (
                <>
                  {canRequestClassChanges && (
                    <button type="button" className="secondary-button" onClick={() => openClassRequest("time_change")}>
                      Request new time
                    </button>
                  )}
                  {canRequestClassCancellation && (
                    <button type="button" className="secondary-button" onClick={() => openClassRequest("cancellation")}>
                      Request cancel
                    </button>
                  )}
                </>
              )}
              <div className="events-schedule-focus-secondary-actions">
              {selectedEvent.status === "draft" && (
                <button
                  type="button"
                  className="secondary-button"
                  disabled={updatingEventId === selectedEvent.eventId}
                  onClick={() => onUpdateEvent(selectedEvent.eventId, { status: "published" })}
                >
                  Publish
                </button>
              )}
              {selectedEventCanCancel && (
                <button
                  type="button"
                  className={confirmingCancelEventId === selectedEvent.eventId ? "danger-button" : "secondary-button"}
                  disabled={updatingEventId === selectedEvent.eventId}
                  onClick={() => {
                    if (confirmingCancelEventId !== selectedEvent.eventId) {
                      setConfirmingCancelEventId(selectedEvent.eventId);
                      return;
                    }
                    setConfirmingCancelEventId("");
                    onUpdateEvent(selectedEvent.eventId, {
                      status: "cancelled",
                      cancellationReason: "Cancelled by owner"
                    });
                  }}
                >
                  {updatingEventId === selectedEvent.eventId
                    ? "Cancelling..."
                    : confirmingCancelEventId === selectedEvent.eventId
                      ? "Confirm cancel"
                      : "Cancel class"}
                </button>
              )}
              </div>
            </div>
          </section>
        )}
        {selectedEvent && selectedEvent.changeRequest?.status === "pending" && (
          <section className="events-class-request-panel" aria-label="Pending class request">
            <span className="section-kicker">Owner approval</span>
            <strong>
              {selectedEvent.changeRequest.type === "cancellation" ? "Cancellation requested" : "Time change requested"}
            </strong>
            <p>{selectedEvent.changeRequest.reason}</p>
            {selectedEvent.changeRequest.type === "time_change" && (
              <small>{formatEventWindow(selectedEvent.changeRequest.startsAt, selectedEvent.changeRequest.endsAt)}</small>
            )}
          </section>
        )}
        {selectedEvent && classRequestMode && (
          <form className="events-class-request-panel is-editing" onSubmit={submitClassRequest}>
            <div>
              <span className="section-kicker">{classRequestMode === "cancellation" ? "Cancel request" : "Time request"}</span>
              <strong>{classRequestMode === "cancellation" ? "Ask owner to cancel this class" : "Ask owner to approve a new time"}</strong>
            </div>
            {classRequestMode === "time_change" && (
              <div className="form-grid events-class-request-times">
                <label>
                  Starts
                  <input
                    type="datetime-local"
                    value={classRequestDraft.startsAt}
                    onChange={(event) => setClassRequestDraft((current) => ({ ...current, startsAt: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Ends
                  <input
                    type="datetime-local"
                    value={classRequestDraft.endsAt}
                    onChange={(event) => setClassRequestDraft((current) => ({ ...current, endsAt: event.target.value }))}
                    required
                  />
                </label>
              </div>
            )}
            <label>
              Reason
              <textarea
                value={classRequestDraft.reason}
                onChange={(event) => setClassRequestDraft((current) => ({ ...current, reason: event.target.value }))}
                placeholder={classRequestMode === "cancellation" ? "Why should this class be cancelled?" : "Why should the class time change?"}
                required
              />
            </label>
            <div className="form-actions">
              <button type="submit" disabled={updatingEventId === selectedEvent.eventId || !classRequestDraft.reason.trim()}>
                Submit request
              </button>
              <button type="button" className="secondary-button" onClick={() => setClassRequestMode("")}>
                Cancel
              </button>
            </div>
          </form>
        )}
        {selectedEvent && selectedEventAttendanceAvailable && (
          <section className="events-selected-class-workspace" id="events-selected-roster" aria-label="Selected class roster and attendance">
            <div className="events-selected-class-workspace-header">
              <div>
                <span className="section-kicker">Roster & attendance</span>
                <h4>{selectedEventSignoff ? "Signed-off class record" : "Class check-in"}</h4>
              </div>
              <div className="events-selected-class-workspace-actions">
                {!selectedEventSignoff && (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setManualAttendanceOpen((current) => !current)}
                  >
                    {manualAttendanceOpen ? "Close guest form" : "Add guest"}
                  </button>
                )}
                {selectedEvent && !selectedEventSignoff && canSignOffEvent && onSignOffEvent && (
                  <button
                    type="button"
                    disabled={updatingEventId === selectedEvent.eventId}
                    onClick={() => onSignOffEvent(selectedEvent.eventId, {
                      attendanceCount: eventAttendance.length,
                      attendedCount: eventAttendanceSummary?.attended || 0,
                      noShowCount: eventAttendanceSummary?.noShow || 0
                    })}
                  >
                    {updatingEventId === selectedEvent.eventId ? "Signing off..." : "Sign off class"}
                  </button>
                )}
              </div>
            </div>

            <div className="events-selected-class-summary" aria-label="Roster summary">
              <div>
                <span>{selectedEventSignoff ? "Attended" : "Checked in"}</span>
                <strong>{selectedEventSignoff ? (eventAttendanceSummary?.attended || 0) : rosterCheckedInCount}</strong>
              </div>
              {selectedEventSignoff ? (
                <div>
                  <span>No-show</span>
                  <strong>{eventAttendanceSummary?.noShow || 0}</strong>
                </div>
              ) : (
                <div>
                  <span>Remaining</span>
                  <strong>{rosterPendingCheckInCount}</strong>
                </div>
              )}
              <div>
                <span>Members</span>
                <strong>{rosterMemberCount}</strong>
              </div>
              <div>
                <span>Guests</span>
                <strong>{rosterRecordedGuestCount}</strong>
              </div>
            </div>

            {manualAttendanceOpen && !selectedEventSignoff && (
              <form className="events-manual-attendance-form is-inline" onSubmit={submitManualAttendance}>
                <div className="form-grid">
                  <label>
                    Name
                    <input
                      value={manualAttendanceForm.fullName}
                      onChange={(event) => setManualAttendanceForm((current) => ({ ...current, fullName: event.target.value }))}
                      placeholder="Guest name"
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      value={manualAttendanceForm.email}
                      onChange={(event) => setManualAttendanceForm((current) => ({ ...current, email: event.target.value }))}
                      placeholder="Optional"
                    />
                  </label>
                  <label>
                    Outcome
                    <select
                      value={manualAttendanceForm.status}
                      onChange={(event) => setManualAttendanceForm((current) => ({ ...current, status: event.target.value }))}
                    >
                      <option value="attended">Attended</option>
                      <option value="no_show">No-show</option>
                    </select>
                  </label>
                </div>
                <div className="form-actions">
                  <button type="submit" disabled={!manualAttendanceForm.fullName.trim() && !manualAttendanceForm.email.trim()}>
                    Save guest
                  </button>
                </div>
              </form>
            )}

            <div className="events-selected-class-roster">
              <div className="events-selected-class-roster-toolbar">
                <label>
                  Find attendee
                  <input
                    value={eventRegistrationFilters.q}
                    placeholder="Search name or email"
                    onChange={(event) => setEventRegistrationFilters((current) => ({ ...current, q: event.target.value }))}
                  />
                </label>
                {eventRegistrationFilters.q && (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setEventRegistrationFilters((current) => ({ ...current, q: "" }))}
                  >
                    Clear
                  </button>
                )}
              </div>

              {membersLoading && <div className="empty-state">Loading members...</div>}
              {!membersLoading && selectedRosterMembers.length === 0 && (
                <div className="empty-state">{selectedEventIsChapterScoped ? "No active chapter members found." : "No active members found."}</div>
              )}
              {!membersLoading && selectedRosterMembers.length > 0 && (
                <div className={`table-like compact-table events-roster-table${selectedEventSignoff ? " is-read-only" : ""}`} role="table" aria-label="Class roster">
                  <div className="table-row table-head" role="row">
                    <span>Member</span>
                    <span>{selectedEventSignoff ? "Attendance" : "Status"}</span>
                    <span>Range fee</span>
                    {!selectedEventSignoff && <span>Actions</span>}
                  </div>
                  {selectedRosterMembers.map((member) => (
                    (() => {
                      const memberEmail = String(member.email || "").trim();
                      const memberNameKey = normalizeSearch(member.fullName);
                      const recordedAttendance = (member.memberId ? attendanceByMemberId.get(String(member.memberId).trim()) : null)
                        || (memberEmail ? attendanceByEmail.get(memberEmail.toLowerCase()) : null)
                        || (memberNameKey ? attendanceByName.get(memberNameKey) : null);
                      const attendanceLabel = recordedAttendance
                        ? formatLabel(recordedAttendance.status)
                        : selectedEventSignoff
                          ? "Not checked in"
                        : member.memberId || memberEmail
                          ? "Ready"
                          : "Needs email";
                      const rangeFeeStatus = recordedAttendance?.rangeFee?.status || "unpaid";
                      const memberChapterName = member.chapterSnapshot?.name
                        || chapterById.get(member.homeChapterId)?.name
                        || selectedEventChapterNames.join(", ")
                        || "Range-wide";
                      const attendeeName = member.fullName || member.email || "Member";
                      const showEmail = memberEmail && memberEmail !== attendeeName;
                      const attendancePayload = {
                        attendeeType: "member",
                        source: "member",
                        memberId: member.memberId,
                        email: memberEmail,
                        fullName: attendeeName,
                        chapterId: member.homeChapterId || member.chapterId || selectedRosterChapterIds[0] || "",
                        chapterName: memberChapterName,
                        memberSnapshot: {
                          memberId: member.memberId,
                          fullName: member.fullName,
                          email: member.email,
                          memberNumber: member.memberNumber
                        }
                      };

                      return (
                        <div className="table-row events-roster-table-row" role="row" key={member.memberId || memberEmail}>
                          <span>
                            <strong>{attendeeName}</strong>
                            {showEmail && <small>{memberEmail}</small>}
                          </span>
                          <span>{attendanceLabel}</span>
                          <span>
                            {recordedAttendance
                              ? selectedEventSignoff
                                ? rangeFeeRecordLabel(recordedAttendance.rangeFee)
                                : rangeFeeLabel(recordedAttendance.rangeFee)
                              : selectedEventSignoff
                                ? "-"
                                : "Collect range fee"}
                          </span>
                          {!selectedEventSignoff && (
                          <span className="row-actions compact-actions events-roster-actions">
                            {recordedAttendance && (
                              <select
                                className="events-roster-action-select"
                                value=""
                                aria-label={`Update ${attendeeName}`}
                                disabled={Boolean(recordingAttendanceKey)}
                                onChange={(event) => {
                                  const nextAction = event.target.value;
                                  if (nextAction === "paid") {
                                    onUpdateEventAttendance?.(recordedAttendance, { rangeFeeStatus: "paid" });
                                  }
                                  if (nextAction === "waived") {
                                    onUpdateEventAttendance?.(recordedAttendance, { rangeFeeStatus: "waived" });
                                  }
                                  if (nextAction === "attended" || nextAction === "no_show") {
                                    updateAttendanceOutcome(onUpdateEventAttendance, recordedAttendance, nextAction);
                                  }
                                }}
                              >
                                <option value="">Set status</option>
                                {recordedAttendance.status !== "attended" && <option value="attended">Mark attended</option>}
                                {recordedAttendance.status !== "no_show" && <option value="no_show">Mark no-show</option>}
                                {rangeFeeStatus !== "paid" && <option value="paid">Fee paid</option>}
                                {rangeFeeStatus !== "waived" && <option value="waived">Waive fee</option>}
                              </select>
                            )}
                            {!recordedAttendance && (
                              <select
                                className="events-roster-action-select"
                                value=""
                                aria-label={`Record ${attendeeName}`}
                                disabled={!(member.memberId || memberEmail) || Boolean(recordingAttendanceKey)}
                                onChange={(event) => {
                                  const nextStatus = event.target.value;
                                  if (nextStatus === "attended" || nextStatus === "no_show") {
                                    submitAttendanceOutcome(onRecordEventAttendance, attendancePayload, nextStatus);
                                  }
                                }}
                              >
                                <option value="">Set status</option>
                                <option value="attended">Attended</option>
                                <option value="no_show">No-show</option>
                              </select>
                            )}
                          </span>
                          )}
                        </div>
                      );
                    })()
                  ))}
                </div>
              )}
            </div>

            <div className="events-selected-class-attendance">
              <div className="events-selected-class-attendance-header">
                <h4>Attendance record</h4>
                <div className="events-roster-stats">
                  <div>
                    <span>Attended</span>
                    <strong>{eventAttendanceSummary?.attended || 0}</strong>
                  </div>
                  <div>
                    <span>No-show</span>
                    <strong>{eventAttendanceSummary?.noShow || 0}</strong>
                  </div>
                </div>
              </div>
              {eventAttendanceLoading && <div className="empty-state">Loading attendance...</div>}
              {!eventAttendanceLoading && eventAttendance.length === 0 && (
                <div className="empty-state">No attendance recorded yet.</div>
              )}
              {eventAttendance.map((attendance) => (
                (() => {
                  const attendeeName = attendance.fullName || attendance.email || "Guest";
                  const showEmail = attendance.email && attendance.email !== attendeeName;
                  const source = attendanceSource(attendance);

                  return (
                    <div className="events-roster-row events-attendance-row" key={attendance.attendanceId}>
                      <div>
                        <strong>{attendeeName}</strong>
                        {showEmail && <span>{attendance.email}</span>}
                        <em className={`events-attendance-source is-${source}`}>
                          {source === "member" ? "Member" : "Guest"}
                        </em>
                      </div>
                      <div>
                        <strong>{formatLabel(attendance.status)}</strong>
                        <span>{attendance.checkedInAt ? new Date(attendance.checkedInAt).toLocaleString() : "No time"}</span>
                        <span>{rangeFeeLabel(attendance.rangeFee)}</span>
                      </div>
                    </div>
                  );
                })()
              ))}
              {selectedEventSignoff && (
                <div className="events-signoff-report">
                  <strong>Class signed off</strong>
                  <span>
                    {selectedEventSignoff.signedOffByName || "Staff"} closed this class with {selectedEventSignoff.attendanceCount || eventAttendance.length} attendance records.
                  </span>
                  {selectedEventSignoff.note && <small>{selectedEventSignoff.note}</small>}
                </div>
              )}
            </div>
          </section>
        )}
        {(!selectedEvent || scheduleCatalogOpen) && (
        <>
        {!loading && events.length > 0 && (
        <div className={`form-grid compact-form-grid events-schedule-filters ${chapterScopeRequired ? "is-chapter-scoped" : ""}`}>
          <label>
            Status
            <select value={eventFilters.status} onChange={(event) => setEventFilters((current) => ({ ...current, status: event.target.value }))}>
              <option value="">All classes</option>
              <option value="upcoming">Upcoming</option>
              <option value="pending_approval">Needs approval</option>
              <option value="published">Scheduled</option>
              <option value="rejected">Rejected</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
          {chaptersEnabled && !chapterScopeRequired && (
            <label>
              Chapter
              <select value={eventFilters.chapterId || defaultScopedChapterId || ""} onChange={(event) => setEventFilters((current) => ({ ...current, chapterId: event.target.value }))}>
                {!chapterScopeRequired && <option value="">All chapters</option>}
                {chapters.map((chapter) => (
                  <option key={chapter.chapterId} value={chapter.chapterId}>{chapterLabel(chapter)}</option>
                ))}
              </select>
            </label>
          )}
          <label>
            Search
            <input
              value={eventFilters.q}
              onChange={(event) => setEventFilters((current) => ({ ...current, q: event.target.value }))}
              placeholder="Title, location, description"
            />
          </label>
          <button
            type="button"
            className="secondary-button"
            disabled={!eventFilters.status && !eventFilters.q && ((!chapterScopeRequired && !eventFilters.chapterId) || (chapterScopeRequired && eventFilters.chapterId === defaultScopedChapterId))}
            onClick={() => setEventFilters({ status: "", visibility: "", type: "", resourceId: "", chapterId: defaultScopedChapterId, q: "" })}
          >
            Clear
          </button>
        </div>
        )}
        {!loading && events.length === 0 && (
          <div className="events-empty-state">
            <div>
              <h4>No classes yet</h4>
              <span>Create the first class to start scheduling, roster, and attendance workflows.</span>
            </div>
            <button
              type="button"
              className="event-create-program-button"
              onClick={() => {
                setProgramBuilderOpen(true);
                switchEventSection("schedule");
              }}
            >
              Create first class
            </button>
          </div>
        )}
        {!loading && events.length > 0 && visibleScheduleEvents.length === 0 && <div className="empty-state">No classes match the current filters.</div>}
        {visibleScheduleEvents.map((item) => {
          const chapterNames = eventChapterIds(item).map((chapterId) => chapterById.get(chapterId)?.name || item.chapterSnapshot?.name || "Chapter");
          const itemIsChapterScoped = chapterNames.length > 0;
          const instructorNames = item.instructors?.length > 0
            ? item.instructors.map(displayInstructorName).join(", ")
            : (itemIsChapterScoped ? "Chapter lead not assigned" : "Instructor not assigned");
          return (
          <div
            className="event-program-card"
            key={item.eventId}
            role="button"
            tabIndex={0}
            onClick={() => selectScheduleEvent(item.eventId)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                selectScheduleEvent(item.eventId);
              }
            }}
          >
            <div className="event-date-block">
              <span>{formatEventDay(item.startsAt)}</span>
              <strong>{formatEventTimeRange(item.startsAt, item.endsAt)}</strong>
            </div>
            <div className="event-program-main">
              <div className="event-program-title">
                <strong>{displayProgramTitle(item.title)}</strong>
                <em className={`event-program-status is-${eventStatusKey(item)}`}>{eventStatusLabel(item)}</em>
              </div>
              <div className={`event-program-facts ${chapterScopeRequired ? "is-chapter-scoped" : ""}`}>
                <span>{item.location || "Location not set"}</span>
                {!chapterScopeRequired && <span>{chapterNames.length > 0 ? chapterNames.join(", ") : unscopedClassLabel}</span>}
                <span>{itemIsChapterScoped || chapterScopeRequired ? `Lead: ${instructorNames}` : instructorNames}</span>
              </div>
              {item.status === "cancelled" && item.cancellationReason && (
                <span className="event-warning-text">Cancellation: {item.cancellationReason}</span>
              )}
              {item.changeRequest?.status === "pending" && (
                <span className="event-warning-text">
                  {item.changeRequest.type === "cancellation" ? "Cancellation requested" : "Time change requested"}
                  {item.changeRequest.reason ? `: ${item.changeRequest.reason}` : ""}
                </span>
              )}
            </div>
            <div className="event-program-row-action" aria-hidden="true" />
          </div>
          );
        })}
        {events.length > 0 && (
        <div className="events-schedule-export-row">
          <button type="button" className="secondary-button" disabled={exportingEvents || events.length === 0} onClick={onExportEvents}>
            {exportingEvents ? "Exporting..." : "Export"}
          </button>
        </div>
        )}
        </>
        )}
      </div>

      {activeEventSection === "roster" && !selectedEventId && (
        <div className="events-roster-picker">
          <div className="panel-header">
            <div>
              <h3>Open a class first</h3>
              <p>Choose a class from the Classes list, then open its roster from the class summary.</p>
            </div>
          </div>
          <button type="button" className="primary-button" onClick={() => switchEventSection("schedule")}>
            Go to classes
          </button>
        </div>
      )}

      {activeEventSection === "roster" && selectedEventId && (
        <div className="membership-type-management">
          <section className="events-roster-command">
            <div className="events-roster-command-main">
              <span className="section-kicker">Selected class</span>
              <strong>{displayProgramTitle(selectedEvent?.title || "Selected event")}</strong>
                  {selectedEvent && (
                    <div className="events-roster-command-facts">
                      <span>{formatEventWindow(selectedEvent.startsAt, selectedEvent.endsAt)}</span>
                      <span>{selectedEvent.location || "No location"}</span>
                      <span>{selectedEventChapterNames.length > 0 ? selectedEventChapterNames.join(", ") : "Organization-wide"}</span>
                    </div>
                  )}
              {selectedEvent && (
                <div className={`events-signoff-state ${selectedEventSignoff ? "is-complete" : "is-pending"}`}>
                  <strong>{selectedEventSignoffTitle}</strong>
                  <span>{selectedEventSignoffDetail}</span>
                </div>
              )}
	            </div>
            <div className="events-roster-command-side">
              <div className="events-roster-command-stats" aria-label="Roster check-in summary">
                <div>
                  <span>{selectedEventSignoff ? "Attended" : "Checked in"}</span>
                  <strong>{selectedEventSignoff ? (eventAttendanceSummary?.attended || 0) : rosterCheckedInCount}</strong>
                </div>
                {selectedEventSignoff ? (
                  <div>
                    <span>No-show</span>
                    <strong>{eventAttendanceSummary?.noShow || 0}</strong>
                  </div>
                ) : (
                  <div>
                    <span>Not checked in</span>
                    <strong>{rosterPendingCheckInCount}</strong>
                  </div>
                )}
                <div>
                  <span>Guests</span>
                  <strong>{rosterRecordedGuestCount}</strong>
                </div>
              </div>
              <div className="events-roster-command-actions">
                {selectedEvent && !selectedEventSignoff && canSignOffEvent && onSignOffEvent && (
                  <button
                    type="button"
                    disabled={updatingEventId === selectedEvent.eventId}
                    onClick={() => onSignOffEvent(selectedEvent.eventId, {
                      attendanceCount: eventAttendance.length,
                      attendedCount: eventAttendanceSummary?.attended || 0,
                      noShowCount: eventAttendanceSummary?.noShow || 0
                    })}
                  >
                    {updatingEventId === selectedEvent.eventId ? "Signing off..." : "Sign off class"}
                  </button>
                )}
                <button type="button" className="secondary-button" onClick={() => {
                  setSelectedEventId("");
                  updateSelectedEventUrl("", "roster");
                }}>
                  Choose another class
                </button>
              </div>
            </div>
          </section>
          <div className="membership-settings-panel events-checklist-panel">
            <div className="panel-header events-checklist-header">
              <div>
                <h3>{rosterWorklistTitle}</h3>
                <p>{rosterWorklistSummary}</p>
              </div>
              <div className="events-checklist-header-actions">
                {!selectedEventSignoff && (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setManualAttendanceOpen((current) => !current)}
                  >
                    {manualAttendanceOpen ? "Close guest form" : "Add guest"}
                  </button>
                )}
                {!rosterChecklistOpen && (
                  <button
                    type="button"
                    className="secondary-button events-checklist-toggle"
                    onClick={() => setRosterChecklistOpen(true)}
                  >
                    Show roster
                  </button>
                )}
              </div>
            </div>
            {manualAttendanceOpen && !selectedEventSignoff && (
              <form className="events-manual-attendance-form is-inline" onSubmit={submitManualAttendance}>
                <div className="form-grid">
                  <label>
                    Name
                    <input
                      value={manualAttendanceForm.fullName}
                      onChange={(event) => setManualAttendanceForm((current) => ({ ...current, fullName: event.target.value }))}
                      placeholder="Guest name"
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      value={manualAttendanceForm.email}
                      onChange={(event) => setManualAttendanceForm((current) => ({ ...current, email: event.target.value }))}
                      placeholder="Optional"
                    />
                  </label>
                  <label>
                    Outcome
                    <select
                      value={manualAttendanceForm.status}
                      onChange={(event) => setManualAttendanceForm((current) => ({ ...current, status: event.target.value }))}
                    >
                      <option value="attended">Attended</option>
                      <option value="no_show">No-show</option>
                    </select>
                  </label>
                </div>
                <div className="form-actions">
                  <button type="submit" disabled={!manualAttendanceForm.fullName.trim() && !manualAttendanceForm.email.trim()}>
                    Save guest
                  </button>
                </div>
              </form>
            )}
            {rosterChecklistOpen && (
              <>
                <div className={`workspace-filters events-roster-filters events-checklist-filters${selectedEventSignoff ? " is-review" : ""}`}>
                  <label>
                    {rosterSearchLabel}
                    <input
                      value={eventRegistrationFilters.q}
                      placeholder="Search name or email"
                      onChange={(event) => setEventRegistrationFilters((current) => ({ ...current, q: event.target.value }))}
                    />
                  </label>
                </div>
                {membersLoading && <div className="empty-state">Loading members...</div>}
                {!membersLoading && selectedRosterMembers.length === 0 && (
                  <div className="empty-state">{selectedEventIsChapterScoped ? "No active chapter members found." : "No active members found."}</div>
                )}
                {!membersLoading && selectedRosterMembers.length > 0 && (
                  <div className={`table-like compact-table events-roster-table${selectedEventSignoff ? " is-read-only" : ""}`} role="table" aria-label="Chapter member roster">
                    <div className="table-row table-head" role="row">
                      <span>Member</span>
                      <span>{selectedEventSignoff ? "Attendance" : "Status"}</span>
                      <span>Range fee</span>
                      {!selectedEventSignoff && <span>Actions</span>}
                    </div>
                    {selectedRosterMembers.map((member) => (
                      (() => {
                        const memberEmail = String(member.email || "").trim();
                        const memberNameKey = normalizeSearch(member.fullName);
                        const recordedAttendance = (memberEmail ? attendanceByEmail.get(memberEmail.toLowerCase()) : null)
                          || (memberNameKey ? attendanceByName.get(memberNameKey) : null);
                        const attendanceLabel = recordedAttendance
                          ? formatLabel(recordedAttendance.status)
                          : selectedEventSignoff
                            ? "Not checked in"
                          : memberEmail
                            ? "Ready"
                            : "Needs email";
                        const rangeFeeStatus = recordedAttendance?.rangeFee?.status || "unpaid";
                        const memberChapterName = member.chapterSnapshot?.name
                          || chapterById.get(member.homeChapterId)?.name
                          || selectedEventChapterNames.join(", ")
                          || "Organization-wide";
                        const attendeeName = member.fullName || member.email || "Member";
                        const showEmail = memberEmail && memberEmail !== attendeeName;
                        const attendancePayload = {
                          attendeeType: "member",
                          source: "member",
                          memberId: member.memberId,
                          email: memberEmail,
                          fullName: attendeeName,
                          chapterId: member.homeChapterId || member.chapterId || selectedRosterChapterIds[0] || "",
                          chapterName: memberChapterName,
                          memberSnapshot: {
                            memberId: member.memberId,
                            fullName: member.fullName,
                            email: member.email,
                            memberNumber: member.memberNumber
                          }
                        };

                        return (
                          <div className="table-row events-roster-table-row" role="row" key={member.memberId || memberEmail}>
                            <span>
                              <strong>{attendeeName}</strong>
                              {showEmail && <small>{memberEmail}</small>}
                            </span>
                            <span>{attendanceLabel}</span>
                            <span>
                              {recordedAttendance
                                ? selectedEventSignoff
                                  ? rangeFeeRecordLabel(recordedAttendance.rangeFee)
                                  : rangeFeeLabel(recordedAttendance.rangeFee)
                                : selectedEventSignoff
                                  ? "-"
                                  : "Collect range fee"}
                            </span>
                            {!selectedEventSignoff && (
                            <span className="row-actions compact-actions events-roster-actions">
                              {recordedAttendance && selectedEventSignoff && <span className="events-roster-complete">Saved</span>}
                              {recordedAttendance && !selectedEventSignoff && (
                                <select
                                  className="events-roster-action-select"
                                  value=""
                                  aria-label={`Update ${attendeeName}`}
                                  disabled={Boolean(recordingAttendanceKey)}
                                  onChange={(event) => {
                                    const nextAction = event.target.value;
                                    if (nextAction === "paid") {
                                      onUpdateEventAttendance?.(recordedAttendance, { rangeFeeStatus: "paid" });
                                    }
                                    if (nextAction === "waived") {
                                      onUpdateEventAttendance?.(recordedAttendance, { rangeFeeStatus: "waived" });
                                    }
                                    if (nextAction === "attended" || nextAction === "no_show") {
                                      updateAttendanceOutcome(onUpdateEventAttendance, recordedAttendance, nextAction);
                                    }
                                  }}
                                >
                                  <option value="">Set status</option>
                                  {recordedAttendance.status !== "attended" && <option value="attended">Mark attended</option>}
                                  {recordedAttendance.status !== "no_show" && <option value="no_show">Mark no-show</option>}
                                  {rangeFeeStatus !== "paid" && <option value="paid">Fee paid</option>}
                                  {rangeFeeStatus !== "waived" && <option value="waived">Waive fee</option>}
                                </select>
                              )}
                              {!recordedAttendance && selectedEventSignoff && <span className="events-roster-complete is-muted">Closed</span>}
                              {!recordedAttendance && !selectedEventSignoff && (
                                <select
                                  className="events-roster-action-select"
                                  value=""
                                  aria-label={`Record ${attendeeName}`}
                                  disabled={!memberEmail || Boolean(recordingAttendanceKey)}
                                  onChange={(event) => {
                                    const nextStatus = event.target.value;
                                    if (nextStatus === "attended" || nextStatus === "no_show") {
                                      submitAttendanceOutcome(onRecordEventAttendance, attendancePayload, nextStatus);
                                    }
                                  }}
                                >
                                  <option value="">Set status</option>
                                  <option value="attended">Attended</option>
                                  <option value="no_show">No-show</option>
                                </select>
                              )}
                            </span>
                            )}
                          </div>
                        );
                      })()
                    ))}
                  </div>
                )}
              </>
            )}
            {!rosterChecklistOpen && (
              <div className="events-checklist-collapsed">
                <strong>{rosterPendingCheckInCount > 0 ? "Check-in list hidden" : "Check-in complete"}</strong>
                <span>{rosterPendingCheckInCount > 0 ? "Show the list to update member attendance and range fee status." : "All members have been recorded."}</span>
              </div>
            )}
          </div>

          <div className="membership-settings-panel events-attendance-panel">
            <div className="panel-header events-attendance-header">
              <div>
                <h3>Attendance ledger</h3>
              </div>
              <div className="events-attendance-command">
                <div className="events-roster-stats">
                  <div>
                    <span>Attended</span>
                    <strong>{eventAttendanceSummary?.attended || 0}</strong>
                  </div>
                  <div>
                    <span>No-show</span>
                    <strong>{eventAttendanceSummary?.noShow || 0}</strong>
                  </div>
                </div>
              </div>
            </div>
            <div className="workspace-filters events-roster-filters events-attendance-filters">
              <label>
                Outcome
                <select
                  value={eventAttendanceFilters.status}
                  onChange={(event) => setEventAttendanceFilters((current) => ({ ...current, status: event.target.value }))}
                >
                  <option value="">Any outcome</option>
                  <option value="attended">Attended</option>
                  <option value="no_show">No-show</option>
                </select>
              </label>
              <label>
                Search
                <input
                  value={eventAttendanceFilters.q}
                  placeholder="Search name or email"
                  onChange={(event) => setEventAttendanceFilters((current) => ({ ...current, q: event.target.value }))}
                />
              </label>
            </div>
            {eventAttendanceLoading && <div className="empty-state">Loading attendance...</div>}
            {!eventAttendanceLoading && eventAttendance.length === 0 && (
              <div className="empty-state">No attendance recorded yet.</div>
            )}
            {eventAttendance.map((attendance) => (
              (() => {
                const attendeeName = attendance.fullName || attendance.email || "Guest";
                const showEmail = attendance.email && attendance.email !== attendeeName;
                const source = attendanceSource(attendance);

                return (
                  <div className="events-roster-row events-attendance-row" key={attendance.attendanceId}>
                    <div>
                      <strong>{attendeeName}</strong>
                      {showEmail && <span>{attendance.email}</span>}
                      <em className={`events-attendance-source is-${source}`}>
                        {source === "member" ? "Chapter member" : "Guest"}
                      </em>
                    </div>
                    <div>
                      <strong>{formatLabel(attendance.status)}</strong>
                      <span>{attendance.checkedInAt ? new Date(attendance.checkedInAt).toLocaleString() : "No time"}</span>
                      <span>{rangeFeeLabel(attendance.rangeFee)}</span>
                    </div>
                  </div>
                );
              })()
            ))}
            {selectedEventSignoff && (
              <div className="events-signoff-report">
                <strong>Class signed off</strong>
                <span>
                  {selectedEventSignoff.signedOffByName || "Staff"} closed this class with {selectedEventSignoff.attendanceCount || eventAttendance.length} attendance records.
                </span>
                {selectedEventSignoff.note && <small>{selectedEventSignoff.note}</small>}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
